/**
 * ERPAdvancedGrid — Universal Reusable Data Grid Component
 * Extracted from CategoryMasterGrid.js — mandated for ALL ERP data tables.
 *
 * Features:
 *   - Column chooser (show/hide + freeze/unfreeze) via React Portal
 *   - Export menu (CSV, PDF/Print, HTML) via React Portal
 *   - Per-column sorting with direction indicators
 *   - Per-column filter inputs
 *   - Pagination with configurable page sizes, page input, first/prev/next/last
 *   - ERPActionColumn integration (View/Edit/Void/Preview/Print/Export/Email)
 *   - Loading and empty states
 *   - Frozen column support
 *
 * Props:
 *   title           (string)   Screen title
 *   columns         (array)    [{field, header, sortKey?, filterKey?, visible?, frozen?}]
 *   data            (array)    Row objects
 *   total           (number)   Total records (server-side)
 *   idField         (string)   Primary key field name
 *   nameField       (string)   Display name field name
 *   page            (number)   Current page (1-based)
 *   pageSize        (number)   Rows per page
 *   onPageChange    (fn)       (newPage) => void
 *   onPageSizeChange(fn)       (newSize) => void
 *   sortField       (string)   Current sort field
 *   sortDir         (string)   'asc' | 'desc'
 *   onSort          (fn)       (field) => void
 *   filters         (object)   {field: value}
 *   onFilterChange  (fn)       (newFilters) => void
 *   loading         (bool)     Loading state
 *   onAddNew        (fn)       Add new handler
 *   onEdit          (fn)       (row, mode) => void
 *   onVoid          (fn)       (row) => void
 *   testId          (string)   data-testid prefix
 *   pageSizeOptions (array)    [10, 20, 50, 100]
 *   showExport      (bool)     Show export button (default true)
 *   showColumnChooser (bool)   Show column chooser (default true)
 *   showFilters     (bool)     Show column filters (default true)
 *   showAddNew      (bool)     Show Add New button (default true)
 *   exportFileName  (string)   Export file prefix (defaults to title)
 *   headerActions   (node)     Extra JSX in header actions area
 *   onPreview       (fn)       (row) => void
 *   onPrint         (fn)       (row) => void
 *   onRowExport     (fn)       (row) => void
 *   onEmail         (fn)       (row) => void
 *   cellClassName   (fn)       (field, row) => className string
 *   cellRenderer    (fn)       (field, value, row) => ReactNode | null (return null for default)
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus, Download, Search,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  ArrowUpDown, Columns,
  FileText, FileSpreadsheet, Globe
} from 'lucide-react';
import ERPActionColumn from './ERPActionColumn';
import { erpToast } from './ERPToast';
import '../../styles/erp-theme.css';

const DEFAULT_PAGE_SIZES = [10, 20, 50, 100];

const ERPAdvancedGrid = ({
  title,
  columns: initialColumns,
  data,
  total,
  idField,
  nameField,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sortField,
  sortDir,
  onSort,
  filters,
  onFilterChange,
  loading,
  onAddNew,
  onEdit,
  onVoid,
  testId,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  showExport = true,
  showColumnChooser = true,
  showFilters = true,
  showAddNew = true,
  exportFileName,
  headerActions,
  onPreview,
  onPrint,
  onRowExport,
  onEmail,
  cellClassName,
  cellRenderer,
  onRowClick,
  selectedRowId,
  onBack,
  onDeleteColumn,
  storageKey,
  rowStyle,
}) => {
  // ── Column visibility/freeze/order state ──
  const [cols, setCols] = useState(() => {
    const base = initialColumns.map(c => ({
      ...c, visible: c.visible !== false, frozen: c.frozen || false,
    }));
    if (!storageKey) return base;
    try {
      const saved = JSON.parse(localStorage.getItem(`erp_cols_${storageKey}`) || 'null');
      if (saved && Array.isArray(saved)) {
        // Re-apply saved order/visibility/frozen but keep any new fields from base
        const savedMap = new Map(saved.map(s => [s.field, s]));
        const ordered = saved.map(s => { const b = base.find(c => c.field === s.field); return b ? {...b, visible:s.visible, frozen:s.frozen} : null; }).filter(Boolean);
        const newFields = base.filter(b => !savedMap.has(b.field));
        return [...ordered, ...newFields];
      }
    } catch (_) {}
    return base;
  });

  const persistCols = (nextCols) => {
    if (!storageKey) return;
    try { localStorage.setItem(`erp_cols_${storageKey}`, JSON.stringify(nextCols.map(c => ({field:c.field, visible:c.visible, frozen:c.frozen})))); } catch (_) {}
  };

  // Sync when parent changes column *structure* (new fields added/removed).
  // Uses a field-name signature so normal parent re-renders (new array ref, same fields)
  // do NOT reset the user's column visibility preferences.
  const columnsSignature = initialColumns.map(c => c.field).join('|');
  useEffect(() => {
    setCols(initialColumns.map(c => ({
      ...c,
      visible: c.visible !== false,
      frozen: c.frozen || false,
    })));
    // eslint-disable-next-line
  }, [columnsSignature]);

  // ── Column chooser portal ──
  const [showColChooser, setShowColChooser] = useState(false);
  const [colChooserPos, setColChooserPos] = useState({ top: 0, left: 0 });
  const colChooserRef      = useRef(null);   // button wrapper
  const colChooserPanelRef = useRef(null);   // portal panel — MUST be excluded from click-outside

  useEffect(() => {
    if (!showColChooser) return;
    const handler = (e) => {
      const inWrapper = colChooserRef.current?.contains(e.target);
      const inPanel   = colChooserPanelRef.current?.contains(e.target);
      if (!inWrapper && !inPanel) setShowColChooser(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColChooser]);

  // ── Export menu portal ──
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportMenuPos, setExportMenuPos] = useState({ top: 0, left: 0 });
  const exportBtnRef      = useRef(null);
  const exportMenuPanelRef = useRef(null);  // portal panel — MUST be excluded from click-outside

  useEffect(() => {
    if (!showExportMenu) return;
    const handler = (e) => {
      const inWrapper = exportBtnRef.current?.contains(e.target);
      const inPanel   = exportMenuPanelRef.current?.contains(e.target);
      if (!inWrapper && !inPanel) setShowExportMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showExportMenu]);

  // ── Derived values ──
  const visibleCols = cols.filter(c => c.visible);
  const totalPages = Math.ceil(total / pageSize) || 1;
  const fromRow = total > 0 ? (page - 1) * pageSize + 1 : 0;
  const toRow = Math.min(page * pageSize, total);
  const fileName = exportFileName || title?.replace(/ /g, '_') || 'Export';

  // ── Column toggles ──
  const toggleColumn = (field) => {
    setCols(prev => { const next = prev.map(c => c.field === field ? { ...c, visible: !c.visible } : c); persistCols(next); return next; });
  };
  const toggleFrozen = (field) => {
    setCols(prev => { const next = prev.map(c => c.field === field ? { ...c, frozen: !c.frozen } : c); persistCols(next); return next; });
  };
  // Move column left (←) or right (→) in display order — "Pivot"
  const moveColumn = (field, dir) => {
    setCols(prev => {
      const idx = prev.findIndex(c => c.field === field);
      if (idx < 0) return prev;
      const next = dir === 'up' ? Math.max(0, idx - 1) : Math.min(prev.length - 1, idx + 1);
      if (next === idx) return prev;
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      persistCols(arr);
      return arr;
    });
  };
  const resetColumns = () => {
    const base = initialColumns.map(c => ({ ...c, visible: c.visible !== false, frozen: c.frozen || false }));
    if (storageKey) { try { localStorage.removeItem(`erp_cols_${storageKey}`); } catch (_) {} }
    setCols(base);
  };

  // ── Export handlers ──
  const handleExportCSV = () => {
    const headers = visibleCols.map(c => c.header);
    const rows = data.map(r => visibleCols.map(c => `"${(r[c.field] || '').toString().replace(/"/g, '""')}"`));
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${fileName}.csv`; a.click();
    erpToast.success('Exported as CSV');
  };

  const handleExportHTML = () => {
    const headers = visibleCols.map(c => `<th style="border:1px solid #ddd;padding:6px 10px;background:#4A6FA5;color:#fff;font-size:12px">${c.header}</th>`).join('');
    const rows = data.map(r => '<tr>' + visibleCols.map(c => `<td style="border:1px solid #ddd;padding:4px 8px;font-size:12px">${r[c.field] || ''}</td>`).join('') + '</tr>').join('');
    const html = `<!DOCTYPE html><html><head><title>${title}</title></head><body><h2>${title} Export</h2><p>Exported: ${new Date().toLocaleString()} | Records: ${data.length}</p><table style="border-collapse:collapse;width:100%"><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${fileName}.html`; a.click();
    erpToast.success('Exported as HTML');
  };

  const handleExportPDF = () => {
    const headers = visibleCols.map(c => `<th style="border:1px solid #999;padding:6px 10px;background:#4A6FA5;color:#fff;font-size:11px">${c.header}</th>`).join('');
    const rows = data.map((r, i) => `<tr style="background:${i % 2 === 0 ? '#fff' : '#f5f7fa'}">` + visibleCols.map(c => `<td style="border:1px solid #ddd;padding:4px 8px;font-size:11px">${r[c.field] || ''}</td>`).join('') + '</tr>').join('');
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title><style>@page{size:landscape;margin:10mm}body{font-family:Arial,sans-serif}</style></head><body><h3>${title}</h3><p style="font-size:11px">Exported: ${new Date().toLocaleString()} | Records: ${data.length}</p><table style="border-collapse:collapse;width:100%"><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table></body></html>`);
    win.document.close();
    setTimeout(() => { win.print(); }, 500);
    erpToast.success('PDF print dialog opened');
  };

  // ── Default row actions ──
  const defaultPreview = (row) => erpToast.info(`Preview: ${row[idField]}`);
  const defaultPrint = () => window.print();
  const defaultRowExport = (row) => {
    const t = Object.entries(row).map(([k, v]) => `${k}: ${v}`).join('\n');
    const b = new Blob([t], { type: 'text/plain' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = `${row[idField]}.txt`; a.click();
  };
  const defaultEmail = (row) => erpToast.info(`Email: ${row[idField]}`);

  // ── Default cell class ──
  const getDefaultCellClass = (field) => {
    if (field === idField) return 'cell-mono';
    if (field === nameField) return 'cell-bold';
    return '';
  };

  return (
    <div className="erp-module" data-testid={testId || `${title?.replace(/ /g, '-').toLowerCase()}-grid`}>
      {/* ── Header ── */}
      <div className="erp-screen-header">
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {onBack && (
            <button onClick={onBack} data-testid="screen-back-btn"
              className="erp-btn erp-btn-ghost" style={{padding:'3px 8px',fontSize:11,gap:4}}>
              <ChevronLeft size={14} /> BACK
            </button>
          )}
          <span className="erp-screen-title">{title}</span>
        </div>
        <div className="erp-screen-actions">
          {/* Extra header actions (date filter etc) — before Columns */}
          {headerActions}

          {/* Column Chooser */}
          {showColumnChooser && (
            <div className="erp-col-chooser-wrapper" ref={colChooserRef}>
              <button
                className="erp-btn erp-btn-ghost"
                data-testid={`${testId || 'grid'}-column-chooser-btn`}
                onClick={() => {
                  if (!showColChooser && colChooserRef.current) {
                    const r = colChooserRef.current.getBoundingClientRect();
                    setColChooserPos({ top: r.bottom + 4, left: r.right - 310 });
                  }
                  setShowColChooser(!showColChooser);
                }}
              >
                <Columns size={14} /> Columns
              </button>
              {showColChooser && createPortal(
                <div
                  ref={colChooserPanelRef}
                  className="erp-col-chooser-panel"
                  style={{ position: 'fixed', top: colChooserPos.top, left: Math.max(4, colChooserPos.left), zIndex: 9999 }}
                  data-testid={`${testId || 'grid'}-column-chooser-panel`}
                >
                  <div style={{ fontWeight: 700, fontSize: 11, color: '#334155', marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Columns</span>
                    <button onClick={resetColumns} style={{ fontSize: 10, padding: '1px 7px', border: '1px solid #cbd5e1', borderRadius: 4, background: '#f8fafc', cursor: 'pointer', color: '#64748b' }}>Reset</button>
                  </div>
                  {cols.map((c, idx) => (
                    <label key={c.field} style={{ cursor: 'default' }}>
                      <div className="col-left">
                        <input type="checkbox" checked={c.visible} onChange={(e) => { e.stopPropagation(); toggleColumn(c.field); }} />
                        <span style={{ fontSize: '0.8rem' }}>{c.header}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <button
                          className="freeze-action"
                          title="Move Left"
                          disabled={idx === 0}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveColumn(c.field, 'up'); }}
                          style={{ padding: '2px 5px', opacity: idx === 0 ? 0.3 : 1 }}
                        >←</button>
                        <button
                          className="freeze-action"
                          title="Move Right"
                          disabled={idx === cols.length - 1}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveColumn(c.field, 'down'); }}
                          style={{ padding: '2px 5px', opacity: idx === cols.length - 1 ? 0.3 : 1 }}
                        >→</button>
                        <button
                          className={`freeze-action${c.frozen ? ' active' : ''}`}
                          title={c.frozen ? 'Unfreeze this column' : 'Freeze this column'}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFrozen(c.field); }}
                        >
                          {c.frozen ? 'Unfreeze' : 'Freeze'}
                        </button>
                        {onDeleteColumn && (
                          <button
                            className="freeze-action"
                            title="Remove column permanently"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (window.confirm(`Remove "${c.header}" column permanently?`)) { setCols(prev => prev.filter(x => x.field !== c.field)); onDeleteColumn(c.field); } }}
                            style={{ padding: '2px 5px', color: '#ef4444', fontWeight: 700, fontSize: 11 }}
                          >✕</button>
                        )}
                      </div>
                    </label>
                  ))}
                </div>,
                document.body
              )}
            </div>
          )}

          {/* Add New */}
          {showAddNew && onAddNew && (
            <button className="erp-btn erp-btn-primary" onClick={onAddNew} data-testid={`${testId || 'grid'}-add-new-btn`}>
              <Plus size={14} /> Add New
            </button>
          )}

          {/* Export */}
          {showExport && (
            <div ref={exportBtnRef} style={{ position: 'relative', display: 'inline-flex' }}>
              <button
                className="erp-btn erp-btn-ghost"
                onClick={() => {
                  if (!showExportMenu && exportBtnRef.current) {
                    const r = exportBtnRef.current.getBoundingClientRect();
                    setExportMenuPos({ top: r.bottom + 4, left: r.right - 170 });
                  }
                  setShowExportMenu(o => !o);
                }}
                data-testid={`${testId || 'grid'}-export-btn`}
              >
                <Download size={14} /> Export
              </button>
              {showExportMenu && createPortal(
                <div ref={exportMenuPanelRef} className="erp-action-dropdown" style={{ top: exportMenuPos.top, left: exportMenuPos.left }} data-testid={`${testId || 'grid'}-export-dropdown`}>
                  <button className="erp-action-item act-export" onClick={() => { handleExportCSV(); setShowExportMenu(false); }}>
                    <FileSpreadsheet size={14} /><span>Export as CSV</span>
                  </button>
                  <button className="erp-action-item act-preview" onClick={() => { handleExportPDF(); setShowExportMenu(false); }}>
                    <FileText size={14} /><span>Export as PDF</span>
                  </button>
                  <button className="erp-action-item act-view" onClick={() => { handleExportHTML(); setShowExportMenu(false); }}>
                    <Globe size={14} /><span>Export as HTML</span>
                  </button>
                </div>,
                document.body
              )}
            </div>
          )}

        </div>
      </div>

      {/* ── Grid Table ── */}
      <div className="erp-grid-wrapper" style={{ flex: 1, margin: 0, borderRadius: 0 }}>
        <div className="erp-grid-scroll">
          <table className="erp-grid">
            <thead>
              {/* Column Headers */}
              <tr>
                <th style={{ width: 48, position: 'sticky', left: 0, zIndex: 3 }}>Actions</th>
                {visibleCols.map(c => (
                  <th
                    key={c.field}
                    className={onSort ? 'sortable' : undefined}
                    onClick={onSort ? () => onSort(c.sortKey || c.field) : undefined}
                    style={c.frozen ? { position: 'sticky', left: 48, zIndex: 3, background: 'var(--erp-grid-header-bg)' } : undefined}
                  >
                    {c.header}
                    {onSort && (
                      sortField === (c.sortKey || c.field)
                        ? (sortDir === 'asc' ? ' ▲' : ' ▼')
                        : <ArrowUpDown size={10} style={{ marginLeft: 4, opacity: 0.4, display: 'inline' }} />
                    )}
                  </th>
                ))}
              </tr>

              {/* Column Filters */}
              {showFilters && onFilterChange && (
                <tr className="erp-grid-filter-row">
                  <th style={{ position: 'sticky', left: 0, zIndex: 3 }}></th>
                  {visibleCols.map(c => (
                    <th key={c.field} style={c.frozen ? { position: 'sticky', left: 48, zIndex: 3, background: 'var(--erp-grid-filter-bg)' } : undefined}>
                      <div style={{ position: 'relative' }}>
                        <Search size={10} style={{ position: 'absolute', left: 6, top: 6, color: 'var(--erp-text-muted)' }} />
                        <input
                          className="erp-input erp-input-filter"
                          placeholder={c.header}
                          value={(filters || {})[c.filterKey || c.field] || ''}
                          onChange={(e) => {
                            const key = c.filterKey || c.field;
                            onFilterChange({ ...filters, [key]: e.target.value });
                          }}
                          data-testid={`${testId || 'grid'}-filter-${c.field}`}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={visibleCols.length + 1} style={{ padding: 48, textAlign: 'center', color: 'var(--erp-text-muted)' }}>Loading...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={visibleCols.length + 1} style={{ padding: 48, textAlign: 'center', color: 'var(--erp-text-muted)' }}>No records found</td></tr>
              ) : data.map((row) => (
                <tr key={row[idField]} data-testid={`${testId || 'grid'}-row-${row[idField]}`} className={selectedRowId && row[idField] === selectedRowId ? 'selected' : ''} onClick={() => onRowClick && onRowClick(row)} style={{ cursor: onRowClick ? 'pointer' : undefined, ...(rowStyle ? rowStyle(row) : {}) }}>
                  <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'inherit' }}>
                    <ERPActionColumn
                      row={row}
                      onView={onEdit ? () => onEdit(row, 'view') : undefined}
                      onEdit={onEdit ? () => onEdit(row, 'edit') : undefined}
                      onVoid={onVoid ? () => onVoid(row) : undefined}
                      onPreview={onPreview ? () => onPreview(row) : () => defaultPreview(row)}
                      onPrint={onPrint ? () => onPrint(row) : defaultPrint}
                      onExport={onRowExport ? () => onRowExport(row) : () => defaultRowExport(row)}
                      onEmail={onEmail ? () => onEmail(row) : () => defaultEmail(row)}
                    />
                  </td>
                  {visibleCols.map(c => {
                    const customRender = cellRenderer ? cellRenderer(c.field, row[c.field], row) : null;
                    const cls = cellClassName
                      ? cellClassName(c.field, row)
                      : getDefaultCellClass(c.field);
                    return (
                      <td
                        key={c.field}
                        className={cls}
                        style={c.frozen ? { position: 'sticky', left: 48, zIndex: 1, background: 'inherit' } : undefined}
                      >
                        {customRender !== null && customRender !== undefined ? customRender : (row[c.field] || '')}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Bottom Pagination ── */}
        <div className="erp-pagination" data-testid={`${testId || 'grid'}-pagination`}>
          <span>{fromRow} to {toRow} from {total} Records</span>
          <div className="erp-pagination-controls">
            <span>Show Rows</span>
            <select
              value={pageSize}
              onChange={(e) => {
                onPageSizeChange(parseInt(e.target.value));
                onPageChange(1);
              }}
              data-testid={`${testId || 'grid'}-page-size`}
            >
              {pageSizeOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span>Page</span>
            <input
              type="number"
              value={page}
              onChange={(e) => onPageChange(Math.max(1, Math.min(totalPages, parseInt(e.target.value) || 1)))}
              data-testid={`${testId || 'grid'}-page-input`}
            />
            <span>of {totalPages}</span>
            <button className="erp-pagination-btn" onClick={() => onPageChange(1)} disabled={page <= 1} data-testid={`${testId || 'grid'}-page-first`}>
              <ChevronsLeft size={11} />
            </button>
            <button className="erp-pagination-btn" onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page <= 1} data-testid={`${testId || 'grid'}-page-prev`}>
              <ChevronLeft size={11} />
            </button>
            <button className="erp-pagination-btn" onClick={() => onPageChange(Math.min(totalPages, page + 1))} disabled={page >= totalPages} data-testid={`${testId || 'grid'}-page-next`}>
              <ChevronRight size={11} />
            </button>
            <button className="erp-pagination-btn" onClick={() => onPageChange(totalPages)} disabled={page >= totalPages} data-testid={`${testId || 'grid'}-page-last`}>
              <ChevronsRight size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ERPAdvancedGrid;
